import { html } from '@polymer/polymer';
import '@polymer/paper-checkbox/paper-checkbox.js';
import { KatapultEditorTemplate } from './katapult-editor-template';
import '../progress-bar/progress-bar';
import * as Comlink from 'comlink';
import proj4 from 'proj4';
import { open } from 'shapefile';
import { Papa } from 'papaparse';
import '../map-styles-editor/node-style-editor.js';
import '../map-styles-editor/map-style-dialogs.js';
import { GeofireTools } from '../../modules/GeofireTools.js';
import { Round } from '../../modules/Round.js';
import '../katapult-tooltip/katapult-tooltip.js';
import '../katapult-elements/katapult-button.js';

class KatapultReferenceLayerEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        input[type='file'] {
          display: none;
        }
        #progress {
          height: 10px;
          margin: 10px;
          --progress-background-color: var(--secondary-color);
        }
        [modified] {
          --paper-input-container-input: {
            font-weight: bold;
          };
        }
        .blueOutlineButton {
          border: 1px solid var(--secondary-color);
          color: var(--secondary-color);
        }
        .strikethrough {
          position: absolute;
          height: 2px;
          width: calc(100% - 20px);
          margin: -1px 10px 0 10px;
          top: 50%;
          background-color: var(--primary-color);
        }
      </style>
      <map-style-dialogs editing-style="{{_itemData.styles}}"></map-style-dialogs>

      <paper-dialog
        id="confirmDialog"
        style="width: 500px;"
        no-cancel-on-esc-key
        no-cancel-on-outside-click
        entry-animation="scale-up-animation"
        exit-animation="fade-out-animation"
      >
        <div title secondary-color>[[confirmTitle]]</div>
        <paper-dialog-scrollable>
          <div>[[confirmBody]]</div>
          <template is="dom-if" if="[[equal(confirmType, 'addProperties')]]">
            <katapult-button name="addPropertiesInput" on-click="openFileInput" style="margin: 15px 0;">Select File</katapult-button>
            <input
              id="addPropertiesInput"
              type="file"
              accept=".dbf,.prj,.shp,.shx,.zip,.csv"
              multiple
              on-change="addPropertiesFileSelected"
            />
            <template is="dom-if" if="[[addingProperties.length]]">
              <paper-table light-grey rounded outline style="overflow:hidden;">
                <paper-row header secondary-color>
                  <paper-cell>Name From File</paper-cell>
                  <paper-cell>Renamed To</paper-cell>
                </paper-row>
                <template is="dom-repeat" items="{{addingProperties}}">
                  <paper-row>
                    <template is="dom-if" if="[[or(item.remove, item.removed)]]">
                      <div class="strikethrough"></div>
                    </template>
                    <paper-cell>[[item.inputName]]</paper-cell>
                    <paper-cell>
                      <paper-input
                        modified$="[[!equal(item.inputName, item.storedName)]]"
                        no-label-float
                        value="{{item.storedName}}"
                      ></paper-input>
                    </paper-cell>
                    <paper-cell icon-button
                      ><katapult-button iconOnly noBorder icon="delete" on-click="removeAddedProperty"></katapult-button
                    ></paper-cell>
                  </paper-row>
                </template>
              </paper-table>
              <katapult-drop-down
                style="margin:0 10px 20px;"
                items="[[addingProperties]]"
                value-path="storedName"
                label-path="storedName"
                open-upwards
                label='Choose the property to match "[[_itemData.uniqueKey]]"'
                value="{{addingPropertiesKey}}"
                invalid="[[addingPropertiesKeyError]]"
                error-message="[[addingPropertiesKeyError]]"
              ></katapult-drop-down>
              <template is="dom-if" if="[[addingPropertiesErrors.length]]">
                <div>Errors:</div>
                <iron-list style="max-height:75px; overflow:auto;" items="[[addingPropertiesErrors]]">
                  <template>
                    <div>[[item]]</div>
                  </template>
                </iron-list>
              </template>
            </template>
          </template>
        </paper-dialog-scrollable>
        <div class="buttons">
          <katapult-button on-click="confirmCancel">Cancel</katapult-button>
          <katapult-button loading="[[confirmDialogLoading]]" color="var(--secondary-color)" on-click="confirmAccepted"
            >[[confirmButtonText]]</katapult-button
          >
        </div>
      </paper-dialog>

      <template is="dom-if" if="[[percentComplete]]">
        <div style="display:flex; flex-direction:column;">
          <div>Uploading - [[round(percentComplete, 1)]]% Complete</div>
          <progress-bar id="progress" value="[[percentComplete]]"></progress-bar>
        </div>
      </template>

      <template is="dom-if" if="[[!canEditLayer(_itemData.isJobSlicingLayer, creatingItem)]]">
        <div style="text-align:center;">This layer can only be deleted</div>
      </template>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <template is="dom-if" if="[[equal(step.id, 'layer_type')]]" restamp>
            <iron-icon icon="help" slot="header">
              <katapult-tooltip style="max-width:400px;" position="right">
                If you are uploading a shapefile layer for job slicing, it must:<br />
                <ul>
                  <li>Contain only polygon features</li>
                  <li>Each polygon feature must have a "grid" property</li>
                  <li>Each "grid" property value should be unique</li>
                </ul>
                If the layer is not for job slicing, it must only contain points. Leave the box unchecked and skip this step.
              </katapult-tooltip>
            </iron-icon>
            <katapult-button on-click="nextStep" slot="current-step-header" style="margin-left: 8px;">Continue</katapult-button>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input style="height:auto;">
                <paper-cell>
                  <paper-checkbox checked="{{_itemData.isJobSlicingLayer}}">This layer contains polygons for job slicing</paper-checkbox>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'file')]]" restamp>
            <iron-icon icon="help" slot="header">
              <katapult-tooltip style="max-width:300px;" position="right">
                <div>A shapefile can be zipped or unzipped and must include .shp, .dbf & .prj files containing point geometry</div>
                <br />
                <div>A CSV must contain "latitude" and "longitude" columns with valid EPSG:4326 coordinates</div>
              </katapult-tooltip>
            </iron-icon>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input style="height:auto;">
                <paper-cell>
                  <katapult-button noBorder noBackground name="mainFileInput" on-click="openFileInput">Select File</katapult-button>
                  <input id="mainFileInput" type="file" accept=".dbf,.prj,.shp,.shx,.zip,.csv" multiple on-change="fileSelected" />
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'properties')]]" restamp>
            <template is="dom-if" if="[[!creatingItem]]">
              <template is="dom-if" if="[[_itemData.uniqueKey]]">
                <katapult-button
                  slot="header"
                  class="blueOutlineButton"
                  size="18"
                  on-click="openAddProperties"
                  icon="add_circle_outline"
                  style="margin-right: 8px;"
                  >Add Properties</katapult-button
                >
              </template>
              <template is="dom-if" if="[[removingProperties]]">
                <katapult-button
                  slot="header"
                  color="var(--secondary-color)"
                  size="18"
                  on-click="confirmRemovingProperties"
                  icon="remove_circle_outline"
                  style="margin-right: 8px;"
                  >Confirm Removing Properties</katapult-button
                >
                <katapult-button slot="header" size="18" style="height:34px; margin-right: 8px;" on-click="toggleRemovingProperties"
                  >Cancel</katapult-button
                >
              </template>
              <template is="dom-if" if="[[!or(removingProperties, percentComplete)]]">
                <katapult-button
                  slot="header"
                  class="blueOutlineButton"
                  size="18"
                  on-click="toggleRemovingProperties"
                  icon="remove_circle_outline"
                  style="margin-right: 8px;"
                  >Remove Properties</katapult-button
                >
              </template>
            </template>
            <template is="dom-if" if="[[creatingItem]]">
              <iron-icon icon="help" slot="header">
                <katapult-tooltip style="max-width:300px;" position="right">
                  <div>
                    The following properties exist in the source file and will be imported to the layer. If you would like to rename any
                    properties before uploading, you can do so below.
                  </div>
                </katapult-tooltip>
              </iron-icon>
            </template>
            <template is="dom-if" if="[[!creatingItem]]">
              <iron-icon icon="help" slot="header">
                <katapult-tooltip style="max-width:300px;" position="right">
                  <div>
                    The following properties existed in the source file and were potentially renamed, then imported to the layer. After
                    upload you can remove properties or add new properties from a shapefile or csv with matching unique key.
                  </div>
                </katapult-tooltip>
              </iron-icon>
            </template>
            <katapult-button on-click="nextStep" slot="current-step-header" style="margin-left: 16px;">Continue</katapult-button>
            <paper-table light-grey rounded outline slot="body" style="overflow:hidden;">
              <paper-row header secondary-color>
                <paper-cell>Name From File</paper-cell>
                <paper-cell>Renamed To</paper-cell>
              </paper-row>
              <template is="dom-repeat" items="{{_itemData.properties}}">
                <paper-row>
                  <template is="dom-if" if="[[or(item.remove, item.removed)]]">
                    <div class="strikethrough"></div>
                  </template>
                  <paper-cell>[[item.inputName]]</paper-cell>
                  <paper-cell>
                    <paper-input
                      modified$="[[!equal(item.inputName, item.storedName)]]"
                      no-label-float
                      value="{{item.storedName}}"
                      disabled="[[!creatingItem]]"
                    ></paper-input>
                  </paper-cell>
                  <template is="dom-if" if="[[removingProperties]]">
                    <paper-cell icon-button
                      ><katapult-button iconOnly noBorder icon="delete" on-click="toggleRemoveProperty"></katapult-button
                    ></paper-cell>
                  </template>
                </paper-row>
              </template>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'unique_key')]]" restamp>
            <iron-icon icon="help" slot="header">
              <katapult-tooltip style="max-width:300px;" position="right">
                <div>
                  A unique key is not required, but allows data to be accessed more quickly using this key and allows for properties to be
                  added to this layer later using this key to match the new data to the existing data.
                </div>
                <br />
                <div>Each record in the source file must have a unique value for this key and duplicates will be skipped.</div>
                <br />
                <div>
                  For example, if you are importing poles and each pole has a unique pole tag, you should use the pole tag as the unique
                  key. If you do not have a unique identifier for each item, do not select a unique key, and we will generate one.
                </div>
              </katapult-tooltip>
            </iron-icon>
            <katapult-button on-click="nextStep" slot="current-step-header" style="margin-left: 8px;">Continue</katapult-button>
            <paper-table style="padding:12px;" light-grey rounded outline slot="body">
              <katapult-drop-down
                items="[[_itemData.properties]]"
                value-path="storedName"
                label-path="storedName"
                label="Unique key (optional)"
                value="{{_itemData.uniqueKey}}"
                disabled="[[!creatingItem]]"
              ></katapult-drop-down>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'map_styles')]]" restamp>
            <template is="dom-if" if="[[creatingItem]]">
              <iron-icon icon="help" slot="header">
                <katapult-tooltip style="max-width:300px;" position="right">
                  <div>
                    Map Style rules allow your reference layer points to have different colors, icons and sizes based on its properties.
                  </div>
                </katapult-tooltip>
              </iron-icon>
            </template>
            <template is="dom-if" if="[[!creatingItem]]">
              <iron-icon icon="help" slot="header">
                <katapult-tooltip style="max-width:300px;" position="right">
                  <div>
                    Map Style rules allow your reference layer points to have different colors, icons and sizes based on its properties.
                  </div>
                  <br />
                  <div>
                    Map Styles can be modified after initial upload and updated, but each record must be updated, so it will take some time
                    for large layers.
                  </div>
                </katapult-tooltip>
              </iron-icon>
            </template>
            <katapult-button on-click="nextStep" slot="current-step-header" style="margin-left: 8px;">Continue</katapult-button>
            <template is="dom-if" if="[[showUpdateStylesButton]]">
              <katapult-button color="var(--secondary-color)" on-click="updateStyles" slot="header" style="margin-left: 8px;"
                >Update Styles</katapult-button
              >
            </template>
            <paper-table light-grey rounded outline slot="body">
              <node-style-editor
                editing-style="{{_itemData.styles}}"
                other-attributes="[[getPropertiesAsAttributes(_itemData.properties, _itemData.properties.length)]]"
                user-group="[[userGroup]]"
              ></node-style-editor>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'advanced_settings')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header" style="margin-left: 8px;">Continue</katapult-button>
            <div slot="body" style="display:flex;flex-direction:column;">
              <div style="font-style: italic; margin: 0 0 20px 20px; font-size: 14px;">
                (These settings only need to be modified in unique scenarios.)
              </div>
              <paper-table style="padding:12px;" light-grey rounded outline>
                <paper-input label="Max Visible Radius" type="number" value="{{_itemData.options.maxRadius}}">
                  <span slot="suffix">m</span>
                </paper-input>
                <paper-input label="Minimum Zoom Level" type="number" value="{{_itemData.options.minZoom}}"></paper-input>
                <paper-input label="Rendering Order" type="number" value="{{_itemData.options.zIndex}}"></paper-input>
              </paper-table>
            </div>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'upload')]]" restamp>
            <iron-icon icon="help" slot="header">
              <katapult-tooltip style="max-width:300px;" position="right">
                <div>
                  Uploading large layers will be a long running process. If you leave this page your upload will stop. Contact Support if
                  you run into any issues!
                </div>
              </katapult-tooltip>
            </iron-icon>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input style="height:auto;">
                <paper-cell>
                  <katapult-button noBorder color="var(--secondary-color)" on-click="upload">Upload</katapult-button>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      helpText: {
        property: Array,
        notify: true,
        readOnly: true,
        value: () => [
          'Reference map layers allow you to upload large datasets then add them by reference to jobs for additional context.',
          'Reference layers added in your company models are available to your company in all models'
        ]
      },
      hideSaveButton: {
        type: Boolean,
        value: false,
        notify: true
      },
      removingProperties: {
        type: Boolean,
        value: false
      },
      showUpdateStylesButton: {
        type: Boolean,
        value: false
      },
      // Used to determine if the shapefile has valid properties before
      // uploading. This is currently only used to check if Aventura
      // uploads properly formatted layers for job slicing
      hasValidProperties: {
        type: Boolean,
        value: false
      }
    };
  }

  static get observers() {
    return [
      'mapStylesChanged(_itemData.styles.*)',
      'setAventuraSteps(_itemData.isJobSlicingLayer, creatingItem)',
      'propertiesChanged(_itemData.properties.*)'
    ];
  }

  constructor() {
    super();
    this.round = Round;
  }

  ready() {
    super.ready();
    this.labelAllowAllCharacters = true;
    this.lastModifiedPath = '_last_modified';
    this.subCollections = ['locations'];

    // If this is Aventura on their server, show the custom Aventura steps
    if (this.isAventuraServer()) {
      this.setAventuraSteps();
    } else {
      // Otherwise, set normal steps
      this.setSteps(this.getNormalSteps());
    }

    this.useWorker = true;
    this.worker = Comlink.wrap(new Worker('/source/_resources/workers/ShapefileWorker.js', { type: 'module' }));
  }

  // Returns an array of "normal" steps for the editor
  getNormalSteps() {
    // An ordered list of all possible steps.
    return [
      {
        id: 'file',
        title: 'Choose Shapefile or CSV To Import',
        if: () => this.get('creatingItem')
      },
      {
        id: 'properties',
        title: 'Layer Properties'
      },
      {
        id: 'unique_key',
        title: 'Unique Key',
        trigger: () => this.get('_itemData.uniqueKey') != null
      },
      {
        id: 'map_styles',
        title: 'Map Styles'
      },
      {
        id: 'advanced_settings',
        title: 'Advanced Settings'
      },
      {
        id: 'upload',
        if: () => this.get('creatingItem') && this.get('hasValidProperties') == true,
        title: 'Upload'
      }
    ];
  }

  cleanup() {
    this.showUpdateStylesButton = false;
    this.overwritingLastModifiedTime = null;
    this.percentComplete = null;
    this.hideSaveButton = false;
    this.hasValidProperties = false;
    this._setCreatingItem(false);
    this._setCurrentIndex(0);
  }

  or(a, b) {
    return a || b;
  }

  labelChanged(label) {
    this.set('_itemData.name', label);
  }

  updateUnsavedChanges() {
    let res = this.compareFlattenedData(this.flattenedItemDataReference, this._flattenedItemData);
    this._setUnsavedChanges(
      ![null, undefined, 100].includes(this.percentComplete) || !!res?.mismatchedKeys.find((x) => x.startsWith('options'))
    );
  }

  mapStylesChanged(change) {
    if (change.path != '_itemData.styles' && !this.creatingItem) this.showUpdateStylesButton = true;
  }

  openFileInput(e) {
    this.shadowRoot.querySelector('#' + e.currentTarget.getAttribute('name')).click();
  }

  async readFile(file, type) {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = async function (e) {
        resolve(reader.result);
      };
      reader[type == 'text' ? 'readAsText' : 'readAsArrayBuffer'](file);
    });
  }

  async fileSelected(e) {
    if (!this.get('_itemData.styles'))
      this.set('_itemData.styles', {
        default: { nodes: [], _counter: 0, fallback: { nodes: { color: '#FFF', icon: 'katapult-map:circle', id: 'mn', size: 18 } } }
      });
    if (!this.get('_itemData.options')) this.set('_itemData.options', { maxRadius: 1000, zIndex: 1, minZoom: 0 });
    if (!this.get('_itemData.source_files')) this.set('_itemData.source_files', []);
    if (!this.get('_itemData.properties')) this.set('_itemData.properties', []);

    let { source_files, properties } = await this.getFileProperties(e);
    this.push('_itemData.source_files', ...source_files);
    this.push('_itemData.properties', ...properties);
    this.nextStep({ noScroll: true });
  }

  async getFileProperties(e) {
    this.files = {};
    this.findFileTypes(Array.from(e.target.files));
    e.target.value = null;

    if (this.files.zip) {
      this.findFileTypes(await this.unzip(this.files.zip));
    }
    let source_files = [];
    let properties = [];

    if (this.files.shp && this.files.dbf) {
      source_files.push(this.files.shp.name, this.files.dbf.name);
      if (this.useWorker) {
        properties.push(
          ...(await this.worker.open(this.files.shp, this.files.dbf)).map((x) => ({ inputName: x.name, storedName: x.name }))
        );
      } else {
        this.source = await open(this.files.shp.stream(), this.files.dbf.stream());
        properties.push(...this.source._dbf._fields.map((x) => ({ inputName: x.name, storedName: x.name })));
      }
    } else if (this.files.csv) {
      source_files.push(this.files.csv.name);

      let results = await new Promise((resolve) => Papa.parse(this.files.csv, { header: true, preview: 1, complete: resolve }));
      properties.push(
        ...results.meta.fields
          .filter((x) => !['latitude', 'longitude'].includes(x?.toLowerCase()))
          .map((x) => ({ inputName: x, storedName: x }))
      );
    }
    return { source_files, properties };
  }

  findFileTypes(fileList) {
    fileList.forEach((x) => {
      let ext = x.name.split('.').pop();
      if (this.files[ext]) {
        console.warn('Multiple files of type', ext);
      } else {
        this.files[ext] = x;
      }
    });
  }

  upload() {
    this.hideSaveButton = true;
    this.save();
    // This is used for the polygon layer updates for Aventura
    this.polygonUpdate = {};

    // Setup map for renamed properties
    this.propertyMap = {};
    (this.addingProperties ?? this._itemData.properties).forEach((item) => {
      this.propertyMap[item.inputName] = item.storedName;
    });

    if (this.files.shp && this.files.dbf) {
      this.importShapefile();
    } else if (this.files.csv) {
      let count = 0;
      Papa.parse(this.files.csv, {
        header: true,
        worker: true,
        chunkSize: 1024 * 1024 * 10,
        step: async (results, parser) => {
          this.percentComplete = (results.meta.cursor / this.files.csv.size) * 100;
          // parser.pause(); //(doesn't work with worker)
          let l = [
            Number(results.data.latitude ?? results.data.Latitude) || 0,
            Number(results.data.longitude ?? results.data.Longitude) || 0
          ];
          delete results.data.latitude;
          delete results.data.longitude;
          delete results.data.Latitude;
          delete results.data.Longitude;
          count++;
          await this.addRecord(l, results.data);
          // parser.resume(); //(doesn't work with worker)
        },
        complete: () => {
          this.doneImport();
        }
      });
      setTimeout(() => this.scrollTo({ top: 0, behavior: 'smooth' }));
    }
  }

  async importShapefile() {
    if (this.files.prj) {
      proj4.defs('shapefileProj', await this.readFile(this.files.prj, 'text'));
      this.push('_itemData.source_files', this.files.prj.name);
    }
    this.count = 0;
    this.next();
    setTimeout(() => this.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  async next() {
    if (this.useWorker) {
      let { result, percentComplete } = await this.worker.next();
      // if we exited the item, stop uploading
      if (!this.itemKey) return;
      this.percentComplete = percentComplete;
      if (result.done) {
        this.doneImport();
      } else {
        await this.importGeoJsonFeature(result.value);
        this.next();
      }
    } else {
      this.percentComplete = (this.source._shp._source._index / this.files.shp.size) * 100;
      // setTimeout(() => {
      this.source.read().then(this.importNext.bind(this));
      // });
    }
  }

  async importNext(result) {
    if (result.done) {
      this.doneImport();
    } else {
      this.count++;
      await this.importGeoJsonFeature(result.value);
      if (this.count < 300) {
        this.next();
      }
    }
  }

  async importGeoJsonFeature(feature) {
    if (feature.geometry.type == 'Point') {
      await this.addRecord(this.xyToLatLong(feature.geometry.coordinates), feature.properties);
    } else if (feature.geometry.type == 'MultiPoint') {
      for (let coord of feature.geometry.coordinates) {
        await this.addRecord(this.xyToLatLong(coord), feature.properties);
      }
    }
    // Check if we are specifically Aventura on their server and should parse polygons
    else if (this.isAventuraServer() && this._itemData?.isJobSlicingLayer && feature.geometry.type == 'Polygon') {
      await this.addRecordForAventura(feature);
    }
  }

  //// Start Aventura specific functions
  //// These functions are only used to allow Aventura to upload polygons to their
  //// master reference shard for job slicing. Once we upgrade this tool to handle
  //// polygons properly with vector tiles, we should take this out/modify it.
  //// Aventura's slicing tool will also need to be updated to work with the vector tiles

  // This function returns true or false if we are an Aventura user on their server
  isAventuraServer() {
    return this.userGroup == 'aventura_group' && config.appName == 'aventura-katapult-pro';
  }

  /**
   * Overrides the template element's delete function to also delete polygon data on the geohash shard
   * @param  {...any} args Any arguments passed in from the parent element
   */
  async delete(...args) {
    // If this is a job slicing layer, then we need to clean up the firebase data
    if (this._itemData.isJobSlicingLayer) {
      let gridRef = firebase.app().database('https://aventura-katapult-pro-master-geohash.firebaseio.com').ref('utility_info/charter/grid');
      // Set up variables for searching
      let deletePromises = [];
      let layerKey = this._itemData.$key;
      let dataLeft = null;
      do {
        // Check if we have data left to delete and delete any found data
        dataLeft = await gridRef
          .orderByChild('layer_key')
          .equalTo(layerKey)
          .limitToFirst(100)
          .once('value')
          .then((s) => s.val());
        for (let key in dataLeft) {
          deletePromises.push(gridRef.child(key).remove());
        }
        await Promise.all(deletePromises);
      } while (dataLeft);
    }
    // Call the parent delete function
    super.delete(...args);
  }

  // This is called whenever _itemData.properties changes and is used to check if the file has valid properties
  propertiesChanged() {
    // Only check properties if we are on Aventura server and are creating the item instead of editing
    if (this.isAventuraServer() && this._itemData?.properties?.length && this.creatingItem) {
      this.hasValidProperties = this._itemData.properties.find((x) => x.inputName?.toLowerCase() == 'grid') != null;
      if (!this.hasValidProperties) {
        alert(
          'The shapefile contains features that are missing a grid property. Please upload a shapefile with polygons that each contain a unique grid property.'
        );
      }
    } else {
      this.hasValidProperties = true;
    }
  }

  // Returns true/false if we can edit the model editor item
  // (we are Aventura and are creating a layer that is a slicing layer)
  canEditLayer() {
    if (this.isAventuraServer()) {
      if (this._itemData?.isJobSlicingLayer) {
        return this.creatingItem == true;
      }
    }
    return true;
  }

  // Changes the editor steps to be custom for Aventura workflows
  setAventuraSteps() {
    // Only modify the steps if we are on the Aventura server
    if (this.isAventuraServer()) {
      // Start the step process over
      this._setCurrentIndex(0);

      // If we can't edit the item, then just remove all of the steps
      if (!this.canEditLayer()) {
        this.setSteps([]);
        return;
      }

      // Get the normal steps and specific steps from that list
      let normalSteps = this.getNormalSteps();
      let fileStep = normalSteps.find((x) => x.id == 'file');
      let uploadStep = normalSteps.find((x) => x.id == 'upload');
      // Create a step object for the step that asks if the layer is for job slicing
      let layerTypeStep = {
        id: 'layer_type',
        title: 'Layer Type',
        trigger: () => this.get('_itemData.isJobSlicingLayer')
      };

      // Create an initial steps list
      let steps = [];

      // Add the layer type step if we are creating the item
      if (this.creatingItem) {
        steps.push(layerTypeStep);
      }

      // If the item is a job slicing layer, then also show the file
      // and upload step. Otherwise, just add the normal steps
      if (this._itemData?.isJobSlicingLayer) {
        steps.push(fileStep, uploadStep);
      } else {
        steps.push(...normalSteps);
      }
      // Set custom steps
      this.setSteps(steps);
    }
  }

  // Creates a firebase record for a Aventura polygon feature
  async addRecordForAventura(feature) {
    // Transform properties
    let properties = feature.properties;
    for (let prop in properties) {
      // Override for grid to make it lower case
      if (prop.toLowerCase() == 'grid') {
        let value = properties[prop];
        // Delete old property
        delete properties[prop];
        // Set the new value for the new property
        properties['grid'] = value;
      }
    }

    // Create a key for the feature from the grid property
    let baseKey = feature.properties.grid;
    if (!baseKey) {
      alert('There was an error uploading the shapefile. A polygon feature is missing a grid property');
      throw 'No grid key found';
    }

    // Create the geoData
    let geoData = {
      c: '#000',
      layer_key: this._itemData.$key,
      info: properties,
      last_updated: new Date().getTime(),
      o: 0.7,
      p: 'solid',
      t: 'c',
      w: 7
    };

    await this.addRecordsForLineType(feature, baseKey, geoData);
  }

  // This created a geojson record for the specific type of feature
  // See the GeoJSON spec for details on the format of these features:
  // https://www.rfc-editor.org/rfc/rfc7946
  async addRecordsForLineType(feature, id, geoData) {
    let coordinates = feature.geometry.coordinates;

    if (feature.geometry.type == 'LineString') {
      await this.createLineString(id, geoData, coordinates);
    } else if (feature.geometry.type == 'MultiLineString') {
      await this.createMultiLineString(id, geoData, coordinates);
    } else if (feature.geometry.type == 'Polygon') {
      await this.createPolygon(id, geoData, coordinates);
    }
  }

  // ID format: {id}-{perimeter|hole}-{type index}-{segment index}~{endpoint index}
  async createPolygon(id, geoData, lineStrings) {
    let itemTypeCounter = 0;
    let itemType = 'perimeter';
    for (let i = 0; i < lineStrings.length; i++) {
      // Check if the item was a perimeter
      await this.createLineString(`${id}-${itemType}-${itemTypeCounter}`, geoData, lineStrings[i]);
      itemTypeCounter++;
      // The item counter should reset after the first lineString (the perimeter) and from
      // then on, it will count the number of holes
      if (i == 0) {
        // Reset the counter and mark that the item type is not a hole
        itemTypeCounter = 0;
        itemType = 'hole';
      }
    }
  }

  // ID format: {id}-{line string index}-{segment index}~{endpoint index}
  async createMultiLineString(id, geoData, lineStrings) {
    for (let i = 0; i < lineStrings.length; i++) {
      await this.createLineString(`${id}-${i}`, geoData, lineStrings[i]);
    }
  }

  // ID format: {id}-{segment index}~{endpoint index}
  async createLineString(id, geoData, coordinates) {
    let index = 0;
    for (let i = 0; i < coordinates.length; i++) {
      let pointOne = coordinates[i];
      let pointTwo = coordinates[i + 1];
      if (pointOne && pointTwo) {
        await this.createLineSegment(`${id}-${index}`, geoData, [pointOne, pointTwo]);
        index++;
      }
    }
  }

  // Creates two points, one for each endpoint of the line segment
  async createLineSegment(id, geoData, coordinates) {
    // Convert coordinates to X/Y projection
    coordinates = coordinates.map((x) => this.xyToLatLong(x));
    // The first point has an l2 location of the second coordinate
    geoData.l2 = coordinates[1];
    GeofireTools.update(id + '~1', coordinates[0], JSON.parse(JSON.stringify(geoData)), 10, this.polygonUpdate);
    // The second point has an l2 location of the first coordinate
    geoData.l2 = coordinates[0];
    GeofireTools.update(id + '~2', coordinates[1], JSON.parse(JSON.stringify(geoData)), 10, this.polygonUpdate);
    await this.commitPolygonUpdate();
  }

  /**
   * This runs the built up update when their are more than 500 update entries
   * @param {*} override {Boolean} Set this to force the update to go through even with less than 500 entries
   */
  async commitPolygonUpdate(override) {
    // Check if we have enough update entries to run the update
    if (override == true || Object.keys(this.polygonUpdate).length > 500) {
      await firebase
        .app()
        .database('https://aventura-katapult-pro-master-geohash.firebaseio.com')
        .ref('utility_info/charter/grid')
        .update(this.polygonUpdate);
      // Reset the update object
      this.polygonUpdate = {};
    }
  }
  //// End Aventura specific functions

  xyToLatLong(point) {
    if (!this.files.prj) return point;
    let transformed = proj4(proj4('shapefileProj'), proj4('EPSG:4326'), { x: point[0], y: point[1] });
    return [transformed.y, transformed.x];
  }

  async addRecord(l, d) {
    // Rename Values
    for (let property in d) {
      if (this.propertyMap[property] != property) {
        d[this.propertyMap[property]] = d[property];
        delete d[property];
      }
      if (d[property] == null) {
        delete d[property];
      }
    }
    if (this.addingProperties) {
      let update = {};
      for (let key in d) {
        if (key != this.addingPropertiesKey) {
          update[`d.${key}`] = d[key];
        }
      }
      update['lm'] = firebase.firestore.FieldValue.serverTimestamp();
      try {
        await firebase
          .firestore()
          .doc(`companies/${this.userGroup}/reference_layers/${this.itemKey}/locations/${d[this.addingPropertiesKey]}`)
          .update(update);
        this.successfulAddCount++;
      } catch (e) {
        if (e.code == 'not-found') {
          this.push('addingPropertiesErrors', `"${d[this.addingPropertiesKey]}" Not found`);
        } else {
          this.push('addingPropertiesErrors', `Error adding "${d[this.addingPropertiesKey]}"`);
        }
      }
    } else {
      let attributes = {};
      for (let key in d) {
        attributes[key] = { itemKey: d[key] };
      }
      let item = GeofireTools._getItemGeoStyle('nodes', { attributes }, this._itemData.styles, null);
      item.l = l;
      item.d = d;
      item.lm = firebase.firestore.FieldValue.serverTimestamp();

      item.g = GeofireTools._encodeGeohash(l);
      await firebase
        .firestore()
        .collection(`companies/${this.userGroup}/reference_layers/${this.itemKey}/locations`)
        .doc(d[this._itemData.uniqueKey])
        .set(item);
    }
  }

  async doneImport() {
    // If we are done with reading the shapefile and we are uploading a job slicing layer for
    // Aventura, then we should force the last batch of accumulated updates
    if (this.isAventuraServer() && this._itemData?.isJobSlicingLayer && this.creatingItem) {
      this.commitPolygonUpdate(true);
    }

    if (this.overwritingLastModifiedTime) {
      await this.runOnAll(
        (doc, batch) => {
          batch.delete(doc.ref);
        },
        (query) => query.where('lm', '<', this.overwritingLastModifiedTime)
      );

      this.overwritingLastModifiedTime = null;
    }

    // Save it to update the last modified date
    this.dispatchEvent(new CustomEvent('save-item', { detail: { forcefully: true } }));

    this.percentComplete = 100;
    // setTimeout(() => {
    //   this.percentComplete = null;
    //   this.hideSaveButton = false;
    // }, 3000);
    if (this.addingProperties) {
      this.finishAddingProperties();
    } else {
      this.nextStep();
    }
  }

  async unzip(file) {
    await import('../../js/open-source/zipjs/zip.js');
    zip.workerScriptsPath = '../_resources/js/open-source/zipjs/';
    return new Promise((resolve, reject) => {
      // use a BlobReader to read the zip from a Blob object
      zip.createReader(new zip.BlobReader(file), (reader) => {
        // get all entries from the zip
        reader.getEntries((entries) => {
          (resolve(
            Promise.all(
              entries
                .filter((x) => !x.directory)
                .map(
                  (entry) =>
                    new Promise((resolve) =>
                      entry.getData(new zip.BlobWriter(), (file) => {
                        file.name = entry.filename;
                        resolve(file);
                      })
                    )
                )
            )
          ),
            (error) => reject(error));
        });
      });
    });
  }

  getPropertiesAsAttributes(properties) {
    let attributeDefinitions = {};
    if (properties && Array.isArray(properties)) {
      properties.forEach((item) => {
        attributeDefinitions[item.storedName] = { gui_element: 'textbox', placeholder: 'value' };
      });
    }
    return attributeDefinitions;
  }

  toggleRemovingProperties() {
    this.removingProperties = !this.removingProperties;
    this._itemData.properties.forEach((item, i) => {
      if (item.remove) {
        this.set(`_itemData.properties.${i}.remove`, false);
      }
    });
  }

  toggleRemoveProperty(e) {
    this.set(`_itemData.properties.${e.model.index}.remove`, !this.get(`_itemData.properties.${e.model.index}.remove`));
  }

  async confirmRemovingProperties(e) {
    let button = e.currentTarget;
    let properties = this._itemData.properties.filter((x) => x.remove).map((x) => x.storedName);
    if (properties.length > 0) {
      if (
        await this.confirm({
          title: 'Remove from all items in layer?',
          body: `Removing properties from all items in a layer is a long running operation, requiring all items to be updated and the data at ${properties.join(
            ', '
          )} will not be recoverable.`
        })
      ) {
        button.loading = true;
        let update = {};
        properties.forEach((x) => (update[`d.${x}`] = firebase.firestore.FieldValue.delete()));
        await this.runOnAll((doc, batch) => {
          batch.update(doc.ref, update);
        });
        this._itemData.properties.forEach((item, i) => {
          if (item.remove) {
            delete item.remove;
            item.removed = true;
          }
        });
        await firebase.firestore().doc(`${this.dataPath}/${this.itemKey}`).update({ properties: this._itemData.properties });
      }
    }
    this.toggleRemovingProperties();
    button.loading = false;
  }

  async openAddProperties() {
    if (
      await this.confirm({
        title: 'Add Properties',
        body: `To add properties to an existing layer, select a shapefile or csv with a column matching your unique key "${this._itemData.uniqueKey}"`,
        type: 'addProperties',
        confirmButtonText: 'Add',
        keepOpen: true
      })
    ) {
      let button = this.shadowRoot.querySelector('katapult-button[name="addPropertiesInput"]');
      if (!this.addingProperties) {
        button.style.color = 'var(--paper-red-500)';
        button.style.border = '2px solid var(--paper-red-500)';
      } else if (!this.addingPropertiesKey) {
        this.addingPropertiesKeyError = 'Please Select a Key';
      } else {
        button.style.color = '';
        button.style.border = '';
        this.addingPropertiesKeyError = null;
        this.confirmDialogLoading = true;
        this.addingPropertiesErrors = [];
        this.successfulAddCount = 0;
        this.upload();
      }
    }
  }

  finishAddingProperties() {
    if (this.successfulAddCount > 0) {
      this.addingProperties.splice(
        this.addingProperties.findIndex((x) => x.storedName == this.addingPropertiesKey),
        1
      );
      this.push('_itemData.properties', ...this.addingProperties);
      this.push('_itemData.source_files', ...this.addingPropertiesFiles);
      this.save();
    }
    this.confirmResolve = this.doneAddingProperties.bind(this);
    this.confirmDialogLoading = false;
    this.confirmButtonText = 'Done';
  }

  doneAddingProperties() {
    this.addingProperties = null;
    this.addingPropertiesKey = null;
    this.addingPropertiesFiles = null;
    this.addingPropertiesErrors = [];
    this.$.confirmDialog.close();
  }

  async addPropertiesFileSelected(e) {
    let { source_files, properties } = await this.getFileProperties(e);
    this.addingPropertiesFiles = source_files;
    this.addingProperties = properties;
    this.$.confirmDialog.notifyResize();
  }

  removeAddedProperty(e) {
    this.set(`addingProperties.${e.model.index}.remove`, !this.get(`addingProperties.${e.model.index}.remove`));
  }

  async updateStyles() {
    if (
      await this.confirm({
        title: 'Update Layer Map Styles',
        body: 'Updating styles is a long running process, if you leave this page the update will stop.',
        confirmButtonText: 'Update',
        keepOpen: true
      })
    ) {
      this.confirmDialogLoading = true;
      this.save();
      await this.runOnAll((doc, batch) => {
        let d = doc.data().d;
        let attributes = {};
        for (let key in d) {
          attributes[key] = { itemKey: d[key] };
        }
        let style = GeofireTools._getItemGeoStyle('nodes', { attributes }, this._itemData.styles, null);
        batch.update(doc.ref, style);
      });
      this.confirmResolve = this.doneAddingProperties.bind(this);
      this.confirmDialogLoading = false;
      this.confirmButtonText = 'Done';
    }
  }

  async confirm({ title, body, type, confirmButtonText, keepOpen }) {
    return new Promise((resolve) => {
      this.confirmResolve = resolve;
      this.confirmType = type;
      this.confirmTitle = title;
      this.confirmBody = body;
      this.confirmButtonText = confirmButtonText || 'Confirm';
      this.confirmKeepOpen = keepOpen;
      this.$.confirmDialog.open();
    });
  }

  confirmAccepted(e) {
    if (!this.confirmKeepOpen) this.$.confirmDialog.close();
    this.confirmResolve(true);
  }

  confirmCancel() {
    this.$.confirmDialog.close();
    this.confirmResolve(false);
  }

  async runOnAll(runOnEach, updateQuery) {
    let query = firebase.firestore().collection(`${this.dataPath}/${this.itemKey}/locations`).limit(500);
    if (updateQuery) query = updateQuery(query);
    let batch = firebase.firestore().batch();
    let lastItem = undefined;
    while (true) {
      let s = await (lastItem ? query.startAfter(lastItem) : query).get();
      if (s.docs.length == 0) break;
      s.docs.forEach((doc) => runOnEach(doc, batch));
      lastItem = s.docs[s.docs.length - 1];
      await batch.commit();
      batch = firebase.firestore().batch();
    }
  }
}

customElements.define('katapult-reference-layer-editor', KatapultReferenceLayerEditor);
